/* VAULT choreography — the ONE-PAGE front door.

   Clayton, 2026-08-07: "just a Shop Now button under the vault that starts the
   animation … I want the animation to always play, so if you ever go on the
   homepage it will play the animation if you click shop now."

   🔑 THERE IS NO 'SEEN' MEMORY ANY MORE. The old build wrote a sessionStorage
   key on the first run and sent return visits straight through the door — the
   film was treated as a first impression, not the experience. That key, and
   both of the branches that read it, are deliberately gone. Every click on
   SHOP NOW plays the film. Do not reintroduce a skip-on-return: it is the
   feature, not an optimisation.

   Chrome visibility is now owned by CSS through the single body.vault-film
   class (wordmark, corner badge, CTA, the showroom surround and the stage's
   width all key off it). The old per-element GSAP opacity tweens are gone —
   one source of truth for "is the film running", so nothing can fade out and
   fail to come back.

   DROP MODE (data-drop-open="true"): SHOP NOW → film → THE PULL → navigate to
   data-drop-url — and every rescue path (reduced motion, hidden tab, watchdog,
   skip, video error, mid-film click) lands there too. enterDrop itself stays
   TWEEN-FREE (gsap.set + location.assign, never a timeline): a backgrounded tab
   freezes rAF and a frozen timeline is a stranded visitor — learned the hard
   way. The pull that precedes it IS a tween, so it is raced by a plain
   setTimeout — if rAF is frozen the timer still fires and the visitor still
   gets in. No exit may depend on an animation completing.

   DROP MODE OFF: the film ends by returning to the front door, ready to play
   again (or, if a legacy #shop band is present on the page, by scrolling to
   it). The page itself NEVER redirects or scrolls on load. */
(function () {
  'use strict';
  var V = document.querySelector('.vault');
  if (!V) return;

  var body = document.body;
  var plate = V.querySelector('.v-plate');
  var film = V.querySelector('.v-film');
  var dim = V.querySelector('.v-dim');
  var btn = V.querySelector('#vshop');
  var skip = V.querySelector('#vskip');
  /* Legacy: the standalone shop band is no longer part of the front door, but
     the section still exists and could be placed on this page in the editor. */
  var shop = document.getElementById('shop');

  var RATE = parseFloat(V.dataset.rate || '1.15');
  var SOUND = V.dataset.sound === 'true';
  var DROP = V.dataset.dropOpen === 'true';
  var DROP_URL = V.dataset.dropUrl || '/collections/all';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var opened = false, filming = false, entered = false;

  /* THE EXIT (Clayton, 2026-08-04: "get rid of the bright flash at the end and
     just have it pull you into the site").
     The film's last beat is already a slow dolly toward the doorway — measured,
     the clip does NOT brighten (mean luminance 29.9→32.1, p99 falls 198→193).
     The flash was entirely the old gold .v-bloom layer painted OVER that dolly.
     So the exit CONTINUES the dolly instead of covering it: the scene
     accelerates into the doorway while the black veil closes over it.
     PULL_LEAD is wall-clock seconds of film remaining when the pull starts.
     At the shipped rate 1.3 the film runs 3.21s wall and the OLD path fired
     the bloom at 2.71s. 1.58 + a 0.55s pull navigates at ~2.17s: half a second
     shorter, as asked. The half second comes out of the door's SETTLE, not its
     travel — measured frame by frame, the corridor is fully open and lit from
     frame 46 (1.44s wall) and everything after is deceleration.
     These three are the dial: raise PULL_LEAD to cut more, lower it to keep
     more of the door. */
  var PULL_LEAD = 1.58;
  var PULL_DUR = 0.55;
  var PULL_SCALE = 1.18;

  /* Drop-mode exit. Tween-free by design: gsap.set paints the black veil in
     the same frame (works even with rAF starved), then we navigate. The veil
     stays up while the browser fetches the drop, so the cut reads as the
     doorway closing over you rather than a hard swap — and because it is BLACK
     and the drop page is dark, there is no bright frame anywhere in the handoff. */
  function enterDrop() {
    if (entered) return;
    entered = true;
    opened = true;
    filming = false;
    if (film) { try { film.pause(); } catch (e) {} }
    if (dim) gsap.set(dim, { opacity: 1 });
    if (skip) { skip.style.visibility = 'hidden'; }
    window.location.assign(DROP_URL);
  }

  /* Put the front door back exactly as it was, replayable.
     keepVeil: the caller is fading the veil out itself and must not have it
     snapped to 0 underneath — a hard cut from black to the lit scene is the
     ugliest frame in the sequence. */
  function restore(keepVeil) {
    filming = false;
    body.classList.remove('vault-film');          /* CSS brings the chrome back */
    if (dim && !keepVeil) gsap.set(dim, { opacity: 0 });
    /* drop the pull's scale — clearProps, not scale:1, so nothing is left
       carrying an inline transform the next run would compound */
    if (film) gsap.set(film, { clearProps: 'transform' });
    if (plate) gsap.set(plate, { clearProps: 'transform' });
    if (film) { try { film.pause(); film.currentTime = 0; } catch (e) {} gsap.set(film, { opacity: 0 }); }
  }

  /* The layers the pull moves. During the film the video covers the plate, so
     scaling the plate is invisible; in the no-video fallback the plate IS the
     scene. Scaling both means one code path serves either. */
  function pullLayers() {
    var l = [];
    if (film && film.style.display !== 'none') l.push(film);
    if (plate) l.push(plate);
    return l;
  }

  /* Pure navigation to a legacy shop band, if one is on the page.
     hard=true is the rAF-starved rescue: a hash jump needs no animation
     machinery at all. */
  function goShop(hard) {
    if (!shop) return false;
    var r = shop.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (r.top >= -4 && r.bottom <= vh + 4) return true;
    if (hard) { window.location.hash = '#shop'; return true; }
    var y0 = window.scrollY || 0;
    try { shop.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' }); }
    catch (e) { window.location.hash = '#shop'; return true; }
    setTimeout(function () {
      if (Math.abs((window.scrollY || 0) - y0) < 40 && shop.getBoundingClientRect().top > 60)
        window.location.hash = '#shop';
    }, 700);
    return true;
  }

  /* Graceful film end, drop mode OFF: out of the dark and back to the front
     door — which is now the whole page, so there is nowhere else to go. The
     replay flags are cleared so SHOP NOW works again immediately; the film
     always plays, including the second time. */
  function backToDoor() {
    restore(true);
    if (dim) gsap.to(dim, { opacity: 0, duration: 0.55, ease: 'power1.inOut' });
    if (skip) gsap.to(skip, { opacity: 0, duration: 0.2, onComplete: function () { skip.style.visibility = 'hidden'; } });
    opened = false;
    if (btn) btn.focus({ preventScroll: true });
  }

  function openShop() {
    if (DROP) { enterDrop(); return; }
    if (opened) return;
    opened = true;
    if (film) { try { film.pause(); } catch (e) {} }
    if (goShop(false)) {
      /* legacy band on the page: the veil is at full black here, so dropping
         the pull's scale under it is invisible; THEN lift the veil */
      restore(true);
      if (dim) gsap.to(dim, { opacity: 0, duration: 0.55, ease: 'power1.inOut' });
      if (skip) gsap.to(skip, { opacity: 0, duration: 0.2, onComplete: function () { skip.style.visibility = 'hidden'; } });
      return;
    }
    backToDoor();
  }

  /* Tween-free rescue: the film never really played (hidden tab, dead
     buffer). Clear the fx layers in one frame and settle. */
  function openInstant() {
    if (DROP) { enterDrop(); return; }
    if (opened) return;
    opened = true;
    if (film) { try { film.pause(); } catch (e) {} film.style.display = 'none'; }
    body.classList.remove('vault-film');
    filming = false;
    if (dim) gsap.set(dim, { opacity: 0 });
    if (film) gsap.set(film, { clearProps: 'transform' });
    if (plate) gsap.set(plate, { clearProps: 'transform' });
    if (skip) { skip.style.opacity = 0; skip.style.visibility = 'hidden'; }
    if (!goShop(true)) { if (film) film.style.display = ''; opened = false; }
  }

  /* THE PULL — the exit beat, replacing the old gold bloom.
     The scene accelerates toward the doorway (transform-origin is the door
     anchor, so the growth happens THROUGH the opening, not from the centre of
     the frame) while the black veil closes over it.

     The two easings are deliberately DIFFERENT, and rendering the beat frame
     by frame is what showed why. power2 is cubic, so cubic on both left the
     scene at 1.007× a third of the way in — nothing, nothing, then a lurch.
     The move is quadratic (starts travelling early, one continuous
     acceleration) and only the veil is cubic, so the picture stays readable
     while it moves and the dark arrives late and fast.

     Two safeties:
     - reduced motion gets the veil ONLY. A push-in is exactly the motion those
       visitors opted out of.
     - navigation is NOT left solely to onComplete. A backgrounded tab suspends
       rAF, GSAP stalls, and a stalled timeline is a stranded visitor — so a
       plain setTimeout races the tween and whichever lands first opens. */
  function pullIn() {
    if (opened || entered) return;
    var go = DROP ? enterDrop : openShop;
    if (reduced) {
      gsap.to(dim, { opacity: 1, duration: 0.3, ease: 'power1.in', onComplete: go });
      setTimeout(go, 520);
      return;
    }
    gsap.to(pullLayers(), { scale: PULL_SCALE, duration: PULL_DUR, ease: 'power1.in' });
    gsap.to(dim, { opacity: 1, duration: PULL_DUR * 0.92, ease: 'power2.in', delay: PULL_DUR * 0.08, onComplete: go });
    setTimeout(go, PULL_DUR * 1000 + 260);
  }

  function simpleOpen() {
    /* Reduced motion / video missing: dim, breathe, move on. Still intentional. */
    if (DROP) { enterDrop(); return; }
    if (opened) return;
    body.classList.add('vault-film');
    filming = true;
    var tl = gsap.timeline();
    tl.to(dim, { opacity: 0.55, duration: reduced ? 0.2 : 0.5 }, 0)
      .add(reduced ? openShop : pullIn, reduced ? 0.25 : 0.55);
  }

  function runFilm() {
    if (film.error) { simpleOpen(); return; }
    /* One class does all of it: the stage widens to a full-bleed letterbox, the
       showroom surround fades out, and the wordmark / badge / CTA step back. */
    body.classList.add('vault-film');
    filming = true;
    film.playbackRate = RATE;
    film.muted = !SOUND;

    /* Watchdog: a cold buffer or a blocked autoplay must never leave the
       visitor staring at a locked page. If the film hasn't actually advanced
       shortly after the click, open anyway. */
    var watchdog = setTimeout(function () {
      if (!opened && film.currentTime < 0.15) {
        if (!film.muted) { film.muted = true; film.play(); }   /* one retry, silent */
        setTimeout(function () {
          /* Last resort is deliberately tween-free: if playback is stalled
             because the tab is backgrounded, GSAP is stalled too. */
          if (!opened && film.currentTime < 0.15) openInstant();
        }, 900);
      }
    }, 1600);
    film.addEventListener('timeupdate', function clearOnce() {
      if (film.currentTime > 0.15) { clearTimeout(watchdog); film.removeEventListener('timeupdate', clearOnce); }
    });

    var tl = gsap.timeline();
    /* the lights flicker as the door takes the load — the only fx tween left
       here, because everything else is CSS off the body class */
    tl.to(dim, { opacity: 0.4, duration: 0.4, yoyo: true, repeat: 1 }, 0)
      .set(film, { opacity: 1 }, 0.18)
      .add(function () {
        var p = film.play();
        if (p && p.catch) p.catch(function () { film.muted = true; film.play().catch(simpleOpen); });
      }, 0.2)
      .to(skip, { opacity: 1, visibility: 'visible', duration: 0.3 }, 1.2);

    /* end beat keyed to real playback time */
    film.addEventListener('timeupdate', function beat() {
      if (!filming || !film.duration) return;
      var left = (film.duration - film.currentTime) / RATE;
      if (left <= PULL_LEAD) { film.removeEventListener('timeupdate', beat); pullIn(); }
    });
    film.addEventListener('ended', openShop);
    film.addEventListener('error', simpleOpen);
    /* a click anywhere mid-film = "yes yes, let me in" */
    V.addEventListener('click', function (e) {
      if (filming && !opened && e.target !== btn) { e.preventDefault(); pullIn(); }
    });
  }

  /* The stage grows from the middle of the viewport, so play it from the top.
     Native smooth scroll up if the visitor had drifted to the footer; the
     timeout proceeds regardless so a throttled tab can never strand the click. */
  function fromTop(fn) {
    var y = window.scrollY || window.pageYOffset || 0;
    if (y < 4) { fn(); return; }
    try { window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' }); }
    catch (e) { window.scrollTo(0, 0); }
    var done = false;
    function go() { if (done) return; done = true; window.removeEventListener('scroll', chk); fn(); }
    function chk() { if ((window.scrollY || 0) < 4) go(); }
    window.addEventListener('scroll', chk, { passive: true });
    setTimeout(go, 560);
  }

  /* Warm the film while the visitor is still reading the landing, so the
     click lands on a buffered video instead of starting the download. */
  if (film && !reduced) {
    if (film.readyState < 2) film.load();
    ['pointerdown', 'pointerover', 'touchstart'].forEach(function (ev) {
      btn && btn.addEventListener(ev, function () {
        if (film.readyState < 2) film.load();
      }, { once: true });
    });
  }

  if (btn) btn.addEventListener('click', function () {
    if (filming || entered) return;
    /* NO 'seen' CHECK. Every click plays the film — that is the ask, and the
       reason the sessionStorage key was removed rather than merely bypassed. */
    /* A hidden tab suspends both video playback and rAF — there is no film to
       watch and no animation to see, so go straight in. */
    if (document.hidden) { openInstant(); return; }
    if (reduced || !film || !film.canPlayType || film.canPlayType('video/mp4') === '') { fromTop(simpleOpen); return; }
    fromTop(function () {
      if (opened || filming) return;
      if (film.readyState >= 2) { runFilm(); return; }
      /* still cold: give it a beat, then roll either way — the watchdog covers us */
      film.load();
      var armed = false;
      var go = function () { if (!armed) { armed = true; runFilm(); } };
      film.addEventListener('canplay', go, { once: true });
      setTimeout(go, 700);
    });
  });

  if (skip) skip.addEventListener('click', function (e) { e.stopPropagation(); if (!opened) pullIn(); });

  /* deterministic stepping for harness/debug (pane rAF is throttled; wall-clock
     runs are verified in a real browser) */
  window.__vault = { shop: openShop, simple: simpleOpen, film: runFilm, instant: openInstant, drop: enterDrop,
    pull: pullIn, timing: { lead: PULL_LEAD, dur: PULL_DUR, scale: PULL_SCALE } };
})();
